import { PRODUCTION_APP_WEB_BASE_URL, STAGING_APP_WEB_BASE_URL } from '@/services/app-web-base-url';
import { RouteName } from '@/router';

export type ParsedDeepLink = {
  path: string;
  query: Record<string, string>;
  fullPath: string;
  postId?: string;
  userId?: string;
  environment: 'staging' | 'production' | 'unknown';
};

const STAGING_HOST = new URL(STAGING_APP_WEB_BASE_URL).host;
const PRODUCTION_HOST = new URL(PRODUCTION_APP_WEB_BASE_URL).host;
const PENDING_REDIRECT_KEY = 'gyming.pendingDeepLink';
export const POST_VIEW_ENTRY_QUERY = 'entry';
export const POST_VIEW_EXTERNAL_ENTRY = 'external';
export const POST_VIEW_PUSH_ENTRY = 'push';

const knownHosts = new Set([STAGING_HOST, PRODUCTION_HOST]);

const resolveEnvironment = (host: string): ParsedDeepLink['environment'] => {
  if (host === STAGING_HOST) return 'staging';
  if (host === PRODUCTION_HOST) return 'production';
  return 'unknown';
};

const queryFromSearchParams = (params: URLSearchParams): Record<string, string> => {
  const query: Record<string, string> = {};
  params.forEach((value, key) => {
    query[key] = value;
  });
  return query;
};

const toFullPath = (path: string, query: Record<string, string>) => {
  const search = new URLSearchParams(query).toString();
  return search ? `${path}?${search}` : path;
};

/**
 * Parses Capacitor appUrlOpen / getLaunchUrl payloads into a Vue Router location.
 * Invalid or empty URLs return null and must not crash routing.
 */
export const parseIncomingAppUrl = (rawUrl: string | null | undefined): ParsedDeepLink | null => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  try {
    const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed);
    if (!hasScheme && !trimmed.startsWith('/')) return null;
    const url = new URL(
      hasScheme ? trimmed : `https://local.invalid${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`
    );
    let path = url.pathname || '/';
    if (hasScheme && url.hostname && !knownHosts.has(url.hostname) && !path.startsWith('/app/')) {
      const reconstructed = `/${url.hostname}${path === '/' ? '' : path}`;
      if (reconstructed.startsWith('/app')) {
        path = reconstructed;
      }
    }
    if (!path.startsWith('/')) return null;
    const query = queryFromSearchParams(url.searchParams);
    const host = hasScheme && url.hostname ? url.hostname : '';
    const environment = host && knownHosts.has(host) ? resolveEnvironment(host) : 'unknown';
    const postViewPrefix = `/${RouteName.App}/${RouteName.UserProfile}/${RouteName.PostView}/`;
    const postId = path.startsWith(postViewPrefix)
      ? decodeURIComponent(path.slice(postViewPrefix.length).split('/')[0] || '')
      : undefined;

    return {
      path,
      query,
      fullPath: toFullPath(path, query),
      postId: postId || undefined,
      userId: query.userId,
      environment: host ? environment : 'unknown',
    };
  } catch {
    return null;
  }
};

export const isSafeInternalRedirect = (value: string | null | undefined): value is string => {
  if (!value) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value.includes('://')) return false;
  return true;
};

/**
 * Incoming links to public auth screens are opened as-is instead of being
 * wrapped as a post-login redirect target. The password-reset continue link
 * (`/login?from=password-reset`) relies on this to keep its query.
 */
export const isPublicAuthEntryPath = (path: string): boolean => path === `/${RouteName.Login}`;

export const readRedirectFromQuery = (query: Record<string, unknown> | undefined): string | null => {
  const raw = query?.redirect;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== 'string') return null;
  return isSafeInternalRedirect(value) ? value : null;
};

export const storePendingDeepLink = (fullPath: string) => {
  if (!isSafeInternalRedirect(fullPath)) return;
  try {
    sessionStorage.setItem(PENDING_REDIRECT_KEY, fullPath);
  } catch {
    // Ignore quota / private-mode failures; in-memory routing still works via login query.
  }
};

export const consumePendingDeepLink = (): string | null => {
  try {
    const stored = sessionStorage.getItem(PENDING_REDIRECT_KEY);
    if (stored) sessionStorage.removeItem(PENDING_REDIRECT_KEY);
    return isSafeInternalRedirect(stored) ? stored : null;
  } catch {
    return null;
  }
};

/** Prefer native launch URL; fall back to a URL queued before the app shell existed. */
export const resolveColdStartDeepLinkUrl = (
  queuedUrl: string | null | undefined,
  launchUrl: string | null | undefined
): string | null => {
  const launch = typeof launchUrl === 'string' && launchUrl.trim() ? launchUrl.trim() : null;
  const queued = typeof queuedUrl === 'string' && queuedUrl.trim() ? queuedUrl.trim() : null;
  return launch || queued || null;
};

const queryValue = (raw: unknown): string | undefined => {
  if (Array.isArray(raw)) return typeof raw[0] === 'string' ? raw[0] : undefined;
  return typeof raw === 'string' ? raw : undefined;
};

export const withExternalPostViewEntry = (
  path: string,
  query: Record<string, string> = {}
): { path: string; query: Record<string, string>; fullPath: string } => {
  const nextQuery = { ...query, [POST_VIEW_ENTRY_QUERY]: POST_VIEW_EXTERNAL_ENTRY };
  return { path, query: nextQuery, fullPath: toFullPath(path, nextQuery) };
};

export const isExternalPostViewEntry = (query: Record<string, unknown> | undefined): boolean =>
  queryValue(query?.[POST_VIEW_ENTRY_QUERY]) === POST_VIEW_EXTERNAL_ENTRY;

/** `entry=push` means the screen was opened from an OS notification, not in-app navigation. */
export const isPushNavigationEntry = (query: Record<string, unknown> | undefined): boolean =>
  queryValue(query?.[POST_VIEW_ENTRY_QUERY]) === POST_VIEW_PUSH_ENTRY;

export const isPushPostViewEntry = isPushNavigationEntry;

/** Chat Details has no external-link entry; only a push tap should leave the stack it landed on. */
export const resolveChatBackAction = (query: Record<string, unknown> | undefined): 'home' | 'back' =>
  isPushNavigationEntry(query) ? 'home' : 'back';

/**
 * In-app Post View keeps the real previous screen; external links must not.
 *
 * A push tap is the same situation: it arrives on whatever stack the app was
 * left on, which can be a transient screen the user never chose to return to
 * (workout finish/feedback/share, post or story composer). Popping one entry
 * would strand them there, so notification entries go home like external ones.
 */
export const resolvePostViewBackAction = (query: Record<string, unknown> | undefined): 'home' | 'back' =>
  isExternalPostViewEntry(query) || isPushPostViewEntry(query) ? 'home' : 'back';
